import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useMessageStore } from './messageStore';
import { useUserStore } from './userStore';

export type LeaseStatus = 'pending' | 'approved' | 'rejected';

interface LeaseRequest {
  id: string;
  listingId: string;
  applicant: {
    id: string;
    name: string;
    email: string;
    phone: string;
    occupation: string;
    monthlyIncome: number;
  };
  listing: {
    title: string;
    price: number;
    location: string;
    image: string;
    ownerId: string;
    ownerName?: string;
    ownerEmail?: string;
    ownerPhone?: string;
  };
  status: LeaseStatus;
  depositPaid?: boolean;
  moveInDate: string;
  moveOutDate: string;
  message: string;
  createdAt: string;
  conversationId?: string;
  securityDeposit?: number;
  moveInInstructions?: string;
  houseRules?: string;
}

interface LeaseStore {
  requests: LeaseRequest[];
  addRequest: (request: Omit<LeaseRequest, 'id' | 'status' | 'createdAt'>) => string;
  updateRequestStatus: (requestId: string, updates: { 
    status: LeaseStatus; 
    depositPaid?: boolean;
    securityDeposit?: number;
    moveInInstructions?: string;
    houseRules?: string;
  }) => void;
  getRequestsByUser: (userId: string, role?: 'applicant' | 'owner') => LeaseRequest[];
  getPendingRequestCount: (userId: string) => number;
  getActiveLeases: (userId: string) => LeaseRequest[];
  hasUserApplied: (userId: string, listingId: string) => boolean;
  resetStore: () => void;
}

const useLeaseStore = create<LeaseStore>()(
  persist(
    (set, get) => ({
      requests: [],
      
      addRequest: (requestData) => {
        const id = `req-${Date.now()}`;
        const { users } = useUserStore.getState();
        const { createConversation, addMessage } = useMessageStore.getState();
        
        // Get owner details from user store
        const owner = users[requestData.listing.ownerId];
        const listing = {
          ...requestData.listing,
          ownerName: owner?.fullName || 'Property Owner',
          ownerEmail: owner?.email,
          ownerPhone: owner?.phone
        };

        // Create conversation between tenant and owner
        const conversationId = createConversation({
          participants: [requestData.applicant.id, requestData.listing.ownerId],
          recipientId: requestData.listing.ownerId,
          recipientName: owner?.fullName || 'Property Owner',
          listingId: requestData.listingId,
          listingTitle: requestData.listing.title
        });

        // Send initial message
        addMessage(conversationId, {
          senderId: 'system',
          content: `🏠 New Sublease Application\n\nA new application has been submitted for ${listing.title}.\n\nPlease review the application details in your dashboard.`,
          isRead: false
        });

        const newRequest: LeaseRequest = {
          id,
          ...requestData,
          listing,
          status: 'pending',
          createdAt: new Date().toISOString(),
          conversationId
        };
        
        set((state) => ({
          requests: [newRequest, ...state.requests]
        }));
        
        return id;
      },

      updateRequestStatus: (requestId, updates) => {
        const { addMessage, createConversation } = useMessageStore.getState();
        const { users } = useUserStore.getState();
        const request = get().requests.find(r => r.id === requestId);

        if (request) {
          // If deposit is being paid, ensure owner details are included
          if (updates.depositPaid) {
            const owner = users[request.listing.ownerId];
            request.listing = {
              ...request.listing,
              ownerName: owner?.fullName || 'Property Owner',
              ownerEmail: owner?.email,
              ownerPhone: owner?.phone
            };
          }

          // Get or create conversation
          let conversationId = request.conversationId;
          if (!conversationId) {
            conversationId = createConversation({
              participants: [request.applicant.id, request.listing.ownerId],
              recipientId: request.listing.ownerId,
              recipientName: request.listing.ownerName || 'Property Owner',
              listingId: request.listingId,
              listingTitle: request.listing.title
            });
          }

          // Send appropriate message based on status update
          if (updates.status === 'approved') {
            const serviceFee = Math.round(request.listing.price * 0.05);
            const message = `
🎉 Application Approved!

Your application for ${request.listing.title} has been approved.

Next Steps:
1. Pay the ${serviceFee} good faith deposit (5% of first month's rent)
2. This deposit is kept by SubleaseHero as a service fee
3. Contact information will be shared after deposit payment
4. Security deposit to be handled directly between parties

View the full details in your dashboard.
`;
            addMessage(conversationId, {
              senderId: 'system',
              content: message,
              isRead: false
            });
          } else if (updates.depositPaid) {
            const message = `
✅ Lease Completed!

The good faith deposit has been received and processed. You can now proceed with the sublease.

📋 Important Details:
• Property: ${request.listing.title}
• Monthly Rent: $${request.listing.price}
• Move-in Date: ${request.moveInDate}
${updates.securityDeposit ? `• Security Deposit: $${updates.securityDeposit}` : ''}

${updates.moveInInstructions ? `\n🔑 Move-in Instructions:\n${updates.moveInInstructions}` : ''}
${updates.houseRules ? `\n📜 House Rules:\n${updates.houseRules}` : ''}

Next Steps:
1. Exchange contact information (now visible in lease details)
2. Coordinate security deposit payment
3. Sign sublease agreement
4. Arrange key pickup and move-in details

Need help? Contact support@subleasehero.com
`;
            addMessage(conversationId, {
              senderId: 'system',
              content: message,
              isRead: false
            });
          }

          set((state) => ({
            requests: state.requests.map(req =>
              req.id === requestId
                ? { 
                    ...req,
                    ...updates,
                    conversationId,
                    listing: {
                      ...req.listing,
                      ownerName: request.listing.ownerName,
                      ownerEmail: request.listing.ownerEmail,
                      ownerPhone: request.listing.ownerPhone
                    }
                  }
                : req
            )
          }));
        }
      },
      
      getRequestsByUser: (userId: string, role = 'applicant') => {
        const { requests } = get();
        return requests
          .filter(request => 
            role === 'applicant' 
              ? request.applicant.id === userId
              : request.listing.ownerId === userId
          )
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },

      getPendingRequestCount: (userId: string) => {
        const { requests } = get();
        return requests.filter(
          request => 
            (request.applicant.id === userId || request.listing.ownerId === userId) &&
            request.status === 'pending'
        ).length;
      },

      hasUserApplied: (userId: string, listingId: string) => {
        const { requests } = get();
        return requests.some(
          request => 
            request.applicant.id === userId && 
            request.listingId === listingId
        );
      },

      getActiveLeases: (userId: string) => {
        const { requests } = get();
        const { users } = useUserStore.getState();
        
        return requests
          .filter(request => {
            const isActive = request.status === 'approved' && request.depositPaid === true;
            const isInvolved = request.applicant.id === userId || request.listing.ownerId === userId;
            
            if (isActive && isInvolved) {
              // Ensure owner information is always included
              const owner = users[request.listing.ownerId];
              request.listing = {
                ...request.listing,
                ownerName: owner?.fullName || 'Property Owner',
                ownerEmail: owner?.email,
                ownerPhone: owner?.phone
              };
              return true;
            }
            return false;
          })
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },

      resetStore: () => set({ requests: [] })
    }),
    {
      name: 'lease-store',
      version: 1
    }
  )
);

export { useLeaseStore };